// Typed client for the Keelson API. Same-origin in production; Vite proxies in dev.

export type ConfidenceLevel = "high" | "medium" | "low";

export interface ColumnSuggestion {
  proposedField: string | null;
  confidence: ConfidenceLevel | null;
  alternatives: string[];
}

export interface PreviewResult {
  headers: string[];
  suggestedMapping: Record<string, string>;
  detailedSuggestions: Record<string, ColumnSuggestion>;
  matchedTemplate: { name: string; bindings: Record<string, string> } | null;
  dataRowCount: number;
}

export interface MappingTemplate {
  id: string;
  name: string;
  headerFingerprint: string;
  bindings: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface RegisterRef {
  registerId: string;
  name: string;
}

export interface RegisterInfo {
  registerId: string;
  name: string;
  createdAt: string;
}

export interface ArrangementRow {
  arrangementRef: string;
  providerCode: string;
  functionRef: string;
  startDate: string | null;
  endDate: string | null;
  annualCostEur: number | null;
  provenance: string;
}

export type CifFlag = "critical" | "important" | "not_critical" | "unassessed";

export interface FunctionRow {
  functionRef: string;
  name: string;
  cifFlag: CifFlag;
  cifLastAssessedOn: string | null;
  cifReason: string | null;
  provenance: string;
}

export interface FunctionPatch {
  cifFlag?: CifFlag;
  cifLastAssessedOn?: string | null;
  cifReason?: string | null;
}

export interface EntityData {
  lei: string;
  name: string;
  country: string;
  refDate: string;
}

export interface Membership {
  tenantId: string;
  tenantName: string;
  role: "owner" | "admin" | "member";
}

export interface Me {
  user: { id: string; email: string; emailVerifiedAt: string | null };
  memberships: Membership[];
  activeTenant: string | null;
  activeRole: "owner" | "admin" | "member" | null;
  csrfToken: string;
}

export interface ImportDiagnostic {
  row: number | null;
  severity: "error" | "warning";
  code: string;
  message: string;
}

export interface ImportResult {
  batchId: string;
  diagnostics: ImportDiagnostic[];
  counts: { arrangements: number; providers: number; functions: number };
  rowCount: number;
}

export interface PromoteResult {
  ok: boolean;
  reason?: string;
  counts?: {
    arrangements: number;
    providers: number;
    functions: number;
    reconcile?: { inserted: number; updated: number };
  };
}

export type Severity = "blocking" | "warning" | "advisory";

export interface Finding {
  ruleId: string;
  family: string;
  severity: Severity;
  location: { entity: string; ref: string | null; field: string | null };
  message: string;
  provenance: string;
}

export interface Readiness {
  taxonomyVersion: string;
  rulesetProvenance: string;
  totals: { blocking: number; warning: number; advisory: number };
  ready: boolean;
  findings: Finding[];
}

export interface ExportResult {
  manifest: {
    taxonomyVersion: string;
    rulesetProvenance: string;
    notForSubmission: boolean;
    entityLei: string;
    templates: { name: string; file: string; rowCount: number }[];
  };
  layer1: { ok: boolean; findings: { code: string; message: string }[] };
  files: { name: string; bytes: number; content: string }[];
}

// The CSRF token from the current session (set on login/register/me). Sent back on every mutating
// request as the x-csrf-token header (double-submit). The session itself rides in an httpOnly cookie.
let csrfToken: string | null = null;
export function setCsrf(token: string | null): void {
  csrfToken = token;
}

async function post<T>(url: string, body: unknown): Promise<T> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (csrfToken !== null) headers["x-csrf-token"] = csrfToken;
  const res = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`${res.status}: ${(await res.text()) || res.statusText}`);
  return (await res.json()) as T;
}

async function get<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`${res.status}: ${(await res.text()) || res.statusText}`);
  return (await res.json()) as T;
}

async function del<T>(url: string): Promise<T> {
  const headers: Record<string, string> = {};
  if (csrfToken !== null) headers["x-csrf-token"] = csrfToken;
  const res = await fetch(url, { method: "DELETE", headers, credentials: "same-origin" });
  if (!res.ok) throw new Error(`${res.status}: ${(await res.text()) || res.statusText}`);
  return (await res.json()) as T;
}

async function patch<T>(url: string, body: unknown): Promise<T> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (csrfToken !== null) headers["x-csrf-token"] = csrfToken;
  const res = await fetch(url, {
    method: "PATCH",
    headers,
    body: JSON.stringify(body),
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`${res.status}: ${(await res.text()) || res.statusText}`);
  return (await res.json()) as T;
}

export interface VerifiedFinding {
  ruleId: string;
  family: string;
  severity: string;
  location: { template: string; row: number; column: string | null };
  message: string;
  provenance: string;
}

export interface VerifiedReadiness {
  taxonomyVersion: string;
  provenance: string;
  totals: { blocking: number; warning: number; advisory: number };
  ready: boolean;
  findings: VerifiedFinding[];
}

export const api = {
  // Auth. Social login is a full-page redirect (/api/auth/login?connection=...).
  // me() is called on load to restore the session from the httpOnly cookie.
  me: () => get<Me>("/api/auth/me"),
  // KEE-112: logout is now a POST (CSRF-protected); returns redirectUrl for the client to follow.
  logout: () => post<{ ok: boolean; redirectUrl: string }>("/api/auth/logout", {}),

  // Business (tenant is derived from the session on the server; the client never sends it).
  preview: (csv: string) => post<PreviewResult>("/api/preview", { csv }),
  importTemplates: (registerId: string, sheets: Record<string, string>) =>
    post<{ diagnostics: unknown[]; counts: Record<string, number> }>(
      `/api/registers/${registerId}/import-templates`,
      { sheets },
    ),
  validateVerified: (registerId: string) =>
    post<VerifiedReadiness>(`/api/registers/${registerId}/validate-verified`, {}),
  createRegister: (name: string) => post<RegisterRef>("/api/registers", { name }),
  importCsv: (registerId: string, csv: string, mapping: Record<string, string>) =>
    post<ImportResult>(`/api/registers/${registerId}/import`, { csv, mapping }),
  promote: (registerId: string, batchId: string) =>
    post<PromoteResult>(`/api/registers/${registerId}/promote`, { batchId }),
  validate: (registerId: string) => post<Readiness>(`/api/registers/${registerId}/validate`, {}),
  exportPackage: (registerId: string) => get<ExportResult>(`/api/registers/${registerId}/export`),
  zipUrl: (registerId: string) => `/api/registers/${registerId}/export.zip`,
  listRegisters: () => get<RegisterInfo[]>("/api/registers"),
  listArrangements: (registerId: string) =>
    get<ArrangementRow[]>(`/api/registers/${registerId}/arrangements`),
  listFunctions: (registerId: string) =>
    get<FunctionRow[]>(`/api/registers/${registerId}/functions`),
  listMappingTemplates: () => get<MappingTemplate[]>("/api/mapping-templates"),
  saveMappingTemplate: (name: string, headers: string[], bindings: Record<string, string>) =>
    post<MappingTemplate>("/api/mapping-templates", { name, headers, bindings }),
  deleteMappingTemplate: (name: string) =>
    del<{ ok: true }>(`/api/mapping-templates/${encodeURIComponent(name)}`),
  patchFunction: (registerId: string, functionRef: string, body: FunctionPatch) =>
    patch<{ ok: true }>(
      `/api/registers/${registerId}/functions/${encodeURIComponent(functionRef)}`,
      body,
    ),
  getEntity: (registerId: string) =>
    get<{ entity: EntityData | null }>(`/api/registers/${registerId}/entity`),
  saveEntity: (registerId: string, entity: EntityData) =>
    post<{ ok: true }>(`/api/registers/${registerId}/entity`, entity),
};
